import type { LotLiveState } from '@skinstep/shared';
import { useEffect, useRef, useState } from 'react';
import { getWsBaseUrl } from '../lib/apiClient';
import { getSessionToken } from '../lib/session';

export type WsConnectionStatus = 'idle' | 'connecting' | 'open' | 'closed';

type WsMessage =
  | { type: 'state'; state: LotLiveState }
  | { type: 'bid_placed'; state: LotLiveState }
  | { type: 'lot_closed'; state: LotLiveState }
  | { type: 'pong' };

type Options = {
  enabled: boolean;
  onBidPlaced?: (state: LotLiveState) => void;
  onLotClosed?: (state: LotLiveState) => void;
};

const BASE_BACKOFF_MS = 800;
const MAX_BACKOFF_MS = 15_000;
const MAX_ATTEMPTS = 6;
// Production heartbeat — three platform-level reasons, none of which is the DO:
//  1. Cloudflare closes a WebSocket after ~100s with no frames in either
//     direction (Free/Pro idle timeout); a periodic ping keeps it open.
//  2. NAT/middlebox keepalive — cellular gateways and the TG WebView network
//     stack drop idle connections silently after their own (shorter) timers.
//  3. Dead-socket detection — feeds the pingsSinceLastPong zombie check below.
// The ping is answered by the LotDO's setWebSocketAutoResponse, which replies
// WITHOUT waking the Durable Object from hibernation — so the heartbeat costs us
// nothing server-side and deliberately does NOT keep the DO resident. 15s is the
// standard interval (socket.io, signalr, phoenix), well under the 100s timeout.
const PING_INTERVAL_MS = 15_000;

function isWsMessage(value: unknown): value is WsMessage {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { type?: unknown };
  return (
    v.type === 'state' || v.type === 'bid_placed' || v.type === 'lot_closed' || v.type === 'pong'
  );
}

export function useLotWs(
  lotId: string,
  options: Options,
): {
  liveState: LotLiveState | null;
  status: WsConnectionStatus;
  pushLiveState: (state: LotLiveState) => void;
} {
  const { enabled, onBidPlaced, onLotClosed } = options;
  const [liveState, setLiveState] = useState<LotLiveState | null>(null);
  const [status, setStatus] = useState<WsConnectionStatus>('idle');
  const handlersRef = useRef({ onBidPlaced, onLotClosed });
  handlersRef.current = { onBidPlaced, onLotClosed };

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return;
    }
    const token = getSessionToken();
    if (!token) {
      setStatus('idle');
      return;
    }

    let cancelled = false;
    let socket: WebSocket | null = null;
    let attempt = 0;
    let reconnectTimer: number | null = null;
    let pingTimer: number | null = null;
    let stopped = false;
    let unbindVisibility: (() => void) | null = null;

    const connect = () => {
      if (cancelled) return;
      if (unbindVisibility) {
        unbindVisibility();
        unbindVisibility = null;
      }
      setStatus('connecting');
      const url = `${getWsBaseUrl()}/api/lots/${lotId}/ws?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      socket = ws;

      let pingsSinceLastPong = 0;

      const sendPing = () => {
        if (ws.readyState !== WebSocket.OPEN) return;
        // 2× consecutive pings without a pong = zombie socket: TCP still thinks
        // it's open, but no data is flowing back. Force-close → onclose fires →
        // reconnect path engages. The shorter PONG_TIMEOUT_MS approach was
        // tempting but adds another timer; counting pings is simpler and
        // catches the same case within ~2 intervals.
        if (pingsSinceLastPong >= 2) {
          console.warn('lot-ws.zombie_socket_detected', { lotId });
          ws.close(4000, 'zombie');
          return;
        }
        pingsSinceLastPong += 1;
        ws.send(JSON.stringify({ type: 'ping' }));
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState !== 'visible') return;
        // Phone screen turned back on / TG WebView returned to foreground —
        // mobile OS may have killed our socket silently. Immediate ping
        // surfaces the dead-socket case in <1 interval.
        sendPing();
      };

      ws.onopen = () => {
        if (cancelled) return;
        attempt = 0;
        setStatus('open');
        pingsSinceLastPong = 0;
        if (pingTimer !== null) window.clearInterval(pingTimer);
        pingTimer = window.setInterval(sendPing, PING_INTERVAL_MS);
        if (typeof document !== 'undefined') {
          document.addEventListener('visibilitychange', handleVisibilityChange);
          unbindVisibility = () =>
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
      };

      ws.onmessage = (ev) => {
        if (cancelled) return;
        let parsed: unknown;
        try {
          parsed = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
        } catch {
          console.warn('lot-ws.message.parse_failed', { lotId });
          return;
        }
        if (!isWsMessage(parsed)) return;

        if (parsed.type === 'pong') {
          pingsSinceLastPong = 0;
          return;
        }

        setLiveState(parsed.state);
        if (parsed.type === 'bid_placed') {
          handlersRef.current.onBidPlaced?.(parsed.state);
        } else if (parsed.type === 'lot_closed') {
          handlersRef.current.onLotClosed?.(parsed.state);
          stopped = true;
        }
      };

      ws.onerror = () => {
        if (cancelled) return;
        console.warn('lot-ws.error', { lotId });
      };

      ws.onclose = (ev) => {
        if (cancelled) return;
        setStatus('closed');
        const clean = ev.code === 1000 || ev.code === 1005;
        // We always retry until MAX_ATTEMPTS even if the socket never reached
        // `open`. The api `/ws` route eagerly init's the DO on 409, but a race
        // (or a transient 502 from CF) can still hit us — better to spend a few
        // backoff cycles than to leave the UI silently disconnected.
        if (clean || stopped || attempt >= MAX_ATTEMPTS) return;
        const delay = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
        attempt += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      if (pingTimer !== null) window.clearInterval(pingTimer);
      if (unbindVisibility) unbindVisibility();
      if (socket && socket.readyState <= WebSocket.OPEN) {
        socket.close(1000, 'unmount');
      }
    };
  }, [enabled, lotId]);

  return { liveState, status, pushLiveState: setLiveState };
}
